import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import UserModal from './UserModal';
import NotificationToast from './NotificationToast';
import './UserManagement.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const HierarchyView = ({ onUpdate }) => {
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [fixedRole, setFixedRole] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [expandedManagers, setExpandedManagers] = useState({});
  const [notification, setNotification] = useState('');
  const notificationTimeoutRef = useRef(null);

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const toggleManager = (managerId) => {
    setExpandedManagers(prev => ({
      ...prev,
      [managerId]: !prev[managerId]
    }));
  };

  const isManagerExpanded = (managerId) => {
    return expandedManagers[managerId] !== false; // Default to expanded
  };

  const fetchHierarchy = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users/hierarchy`);
      setHierarchy(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
      setLoading(false);
    }
  };

  const handleCreateManager = () => {
    setEditingUser(null);
    setFixedRole('manager');
    setSelectedManager(null);
    setShowModal(true);
  };

  const handleCreateEmployee = (manager) => {
    setEditingUser(null);
    setFixedRole('employee');
    setSelectedManager(manager);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFixedRole(null);
    setSelectedManager(null);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${BASE_URL}/api/users/${userId}`);
      handleSuccess('User deleted successfully');
      fetchHierarchy();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting user');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingUser(null);
    setFixedRole(null);
    setSelectedManager(null);
    fetchHierarchy();
    if (onUpdate) onUpdate();
  };

  const handleSuccess = (message) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification(message);
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification('');
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading hierarchy...</p>
    </div>
  );

  return (
    <div className="user-management">
      <NotificationToast message={notification} />
      <div className="header-section">
        <h1>Hierarchical Structure</h1>
        <button className="btn-primary" onClick={handleCreateManager}>
          + Add Manager
        </button>
      </div>

      {hierarchy.length === 0 ? (
        <div className="empty-hierarchy-state">
          <div className="empty-icon">👥</div>
          <h3>No Managers Found</h3>
          <p>Start building your organizational structure by adding your first manager.</p>
          <button className="btn-primary" onClick={handleCreateManager}>
            + Add Your First Manager
          </button>
        </div>
      ) : (
        <div className="users-list">
          {hierarchy.map(manager => {
            const managerId = manager.id || manager._id;
            const isExpanded = isManagerExpanded(managerId);
            
            return (
              <div key={managerId} className="user-card">
                <div className="user-card-header">
                  <div className="user-info-inline">
                    <h3>{manager.name}</h3>
                    <span className="user-username">@{manager.username}</span>
                  </div>
                  <div className="user-actions">
                    <button className="btn-view-details" onClick={() => toggleManager(managerId)}>
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                    <button className="btn-small" onClick={() => handleCreateEmployee(manager)}>
                      + Add Employee
                    </button>
                    <button className="btn-edit-client" onClick={() => handleEdit(manager)}>Edit</button>
                    <button className="btn-delete-client" onClick={() => handleDelete(managerId)}>Delete</button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="hierarchy-section">
                    <h4>Employees</h4>
                    {manager.employees && manager.employees.length > 0 ? (
                      <div className="employees-list-nested">
                        {manager.employees.map(employee => (
                          <div key={employee.id || employee._id} className="employee-card-nested">
                            <div className="user-info-inline">
                              <strong>{employee.name}</strong>
                              <span className="user-username">@{employee.username}</span>
                            </div>
                            <div className="user-actions">
                              <button className="btn-edit-client" onClick={() => handleEdit(employee)}>Edit</button>
                              <button className="btn-delete-client" onClick={() => handleDelete(employee.id || employee._id)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-message">No employees under this manager</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <UserModal
          user={editingUser}
          fixedRole={fixedRole}
          selectedManager={selectedManager}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default HierarchyView;

